import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@heroui/react';
import { renderNotice } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ImportRowNote } from '../../importRows';

type RowDrawerNotesProps = {
    /** Every problem, then every warning, of the last check. */
    notes: ImportRowNote[];
    isExcluded: boolean;
    /** What resolves the problems, under their sentences: candidates, a group's rows. */
    resolution?: React.ReactNode;
};

/**
 * The drawer's problem card, at the top of the body: what the last check
 * said about the row, one sentence each. Problems (what stops the row) in a
 * danger alert, warnings (what the import will do on its own) in a warning
 * alert; a row with neither shows nothing. An excluded row says so first:
 * the import leaves it out, whatever its notes.
 *
 * The sentences are a list beside the title, not inside `Alert.Description`
 * (a `span`). The candidates to pick from and a group's rows to take values
 * from (`resolution`) follow the list inside the same alert.
 */
export const RowDrawerNotes: React.FC<RowDrawerNotesProps> = ({
    notes,
    isExcluded,
    resolution,
}) => {
    const { t } = useTranslation('pages');
    const problems = notes.filter((note) => note.tone === 'danger');
    const warnings = notes.filter((note) => note.tone === 'warning');

    return (
        <>
            {isExcluded ? (
                <Alert
                    {...getNodeTestIdProp('catalog-import-row-drawer-excluded')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('catalogImport.products.drawer.excluded')}
                        </Alert.Title>
                    </Alert.Content>
                </Alert>
            ) : null}
            {problems.length > 0 ? (
                <Alert
                    status="danger"
                    {...getNodeTestIdProp('catalog-import-row-drawer-problems')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('catalogImport.products.drawer.notes.problems')}
                        </Alert.Title>
                        <ul className="flex list-disc flex-col gap-1 ps-4 text-sm">
                            {problems.map((note, index) => (
                                <li
                                    key={`${note.notice.key}-${index}`}
                                    {...getNodeTestIdProp(
                                        `catalog-import-row-drawer-problem-${index}`,
                                    )}
                                >
                                    {renderNotice(t, note.notice)}
                                </li>
                            ))}
                        </ul>
                        {resolution}
                    </Alert.Content>
                </Alert>
            ) : null}
            {warnings.length > 0 ? (
                <Alert
                    status="warning"
                    {...getNodeTestIdProp('catalog-import-row-drawer-warnings')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('catalogImport.products.drawer.notes.warnings')}
                        </Alert.Title>
                        <ul className="flex list-disc flex-col gap-1 ps-4 text-sm">
                            {warnings.map((note, index) => (
                                <li
                                    key={`${note.notice.key}-${index}`}
                                    {...getNodeTestIdProp(
                                        `catalog-import-row-drawer-warning-${index}`,
                                    )}
                                >
                                    {renderNotice(t, note.notice)}
                                </li>
                            ))}
                        </ul>
                    </Alert.Content>
                </Alert>
            ) : null}
        </>
    );
};
